// ─────────────────────────────────────────────────────────────────────────────
// Talent Options — single source of truth for all talent-related field options
// Usage: import { TALENT_OPTIONS } from "@/shared/constants/talentOptions"
// ─────────────────────────────────────────────────────────────────────────────

type Option = { value: string; label: string };

const opt = (value: string, label: string): Option => ({ value, label });

// ── Identity ──────────────────────────────────────────────────────────────────

export const TALENT_TYPES: Option[] = [
  opt("modeling", "Modeling"),
  opt("content_creation", "Content Creation"),
  opt("both", "Both"),
];

export const GENDER_OPTIONS: Option[] = [
  opt("male", "Male"),
  opt("female", "Female"),
];

// ── Languages ─────────────────────────────────────────────────────────────────

export { LANGUAGE_OPTIONS } from "./languages";

// ── Physical Appearance ───────────────────────────────────────────────────────

export const EYE_COLOR_OPTIONS: Option[] = [
  opt("brown", "Brown"),
  opt("blue", "Blue"),
  opt("green", "Green"),
  opt("hazel", "Hazel"),
  opt("gray", "Gray"),
  opt("black", "Black"),
];

export const HAIR_COLOR_OPTIONS: Option[] = [
  opt("black", "Black"),
  opt("brown", "Brown"),
  opt("blonde", "Blonde"),
  opt("red", "Red"),
  opt("gray", "Gray"),
  opt("white", "White"),
];

export const SKIN_TONE_OPTIONS: Option[] = [
  opt("white", "White"),
  opt("albino", "Albino"),
  opt("medium", "Medium"),
  opt("tan", "Tan"),
  opt("brown", "Brown"),
  opt("dark_brown", "Dark Brown"),
  opt("deep_black", "Deep Black"),
];

export const HAIR_TYPE_OPTIONS: Option[] = [
  opt("straight", "Straight"),
  opt("wavy", "Wavy"),
  opt("curly", "Curly"),
  opt("coily", "Coily"),
  opt("bald", "Bald"),
];

export const BODY_TYPE_OPTIONS: Option[] = [
  opt("slim", "Slim"),
  opt("athletic", "Athletic"),
  opt("average", "Average"),
  opt("curvy", "Curvy"),
  opt("over_size", "Over Size"),
];

export const LOOK_ALIKE_OPTIONS: Option[] = [
  opt("celebrity", "Celebrity"),
  opt("foreigner", "Foreigner"),
  opt("khaliji", "Khaliji"),
];

// Values are the discrete codes actually stored in `talents.experience_years`
// (a string column) -- not a literal number of years. Single source of truth
// for both the talent registration/edit forms and the Talents/Casting-Brief
// filter UIs; don't rename a value without updating every consumer.
export const EXPERIENCE_LEVELS: Option[] = [
  opt("1", "Beginner (0-1 years)"),
  opt("2", "Intermediate (2-5 years)"),
  opt("3", "Professional (5+ years)"),
  opt("10", "Expert (10+ years)"),
];

// ── Filter buckets — shared by every "browse talents" sidebar/filter-pill UI ──
// Bucket values must match what useTalentProfile/useTalents' filter logic
// (src/shared/utils/talentFilters.ts) matches against — don't rename a value
// without updating that logic too.

export const AGE_GROUP_OPTIONS: Option[] = [
  opt("0-2", "0 – 2 yrs"),
  opt("3-5", "3 – 5 yrs"),
  opt("6-11", "6 – 11 yrs"),
  opt("12-16", "12 – 16 yrs"),
  opt("17-25", "17 – 25 yrs"),
  opt("26-35", "26 – 35 yrs"),
  opt("36-50", "36 – 50 yrs"),
  opt("+50", "50+ yrs"),
];

export const WEIGHT_BUCKET_OPTIONS: Option[] = [
  opt("under_50", "Under 50 kg"),
  opt("50_60", "50 – 60 kg"),
  opt("60_70", "60 – 70 kg"),
  opt("70_80", "70 – 80 kg"),
  opt("80_90", "80 – 90 kg"),
  opt("over_90", "Over 90 kg"),
];

export const HEIGHT_BUCKET_OPTIONS: Option[] = [
  opt("under_150", "Under 150 cm"),
  opt("150_160", "150 – 160 cm"),
  opt("160_170", "160 – 170 cm"),
  opt("170_180", "170 – 180 cm"),
  opt("180_190", "180 – 190 cm"),
  opt("over_190", "Over 190 cm"),
];

// ── Grouped export (for dynamic form rendering) ───────────────────────────────

export const TALENT_OPTIONS = {
  talentTypes: TALENT_TYPES,
  genders: GENDER_OPTIONS,
  eyeColors: EYE_COLOR_OPTIONS,
  hairColors: HAIR_COLOR_OPTIONS,
  skinTones: SKIN_TONE_OPTIONS,
  hairTypes: HAIR_TYPE_OPTIONS,
  bodyTypes: BODY_TYPE_OPTIONS,
  lookAlike: LOOK_ALIKE_OPTIONS,
  experienceLevels: EXPERIENCE_LEVELS,
} as const;
